import React, { useState, useEffect } from 'react';
import { interventionsApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface HistoryEntry {
  id: number;
  intervention_id: number;
  type_action: 'CREATE' | 'UPDATE' | 'DELETE' | 'VALIDATED' | 'REJECTED' | 'REQUEST_MODIFICATION' |
               'MODIFICATION_APPROVED' | 'MODIFICATION_REJECTED' | 'RESENT' | string;
  nom_champ?: string;
  ancienne_valeur?: string;
  nouvelle_valeur?: string;
  utilisateur_id?: string;
  role_utilisateur?: string;
  modifie_le: string;
  anciennes_donnees?: any;
  nouvelles_donnees?: any;
  numero_semaine?: number;
  prestation_nom?: string;
  contact?: string;
  site_nom?: string;
  technicentre_nom?: string;
  campagne_nom?: string;
  numero_elan?: string;
}

interface HistoriqueGlobalModalProps {
  show: boolean;
  onClose: () => void;
}

// ===== Composant HistoriqueGlobalModal =====
const HistoriqueGlobalModal: React.FC<HistoriqueGlobalModalProps> = ({
  show,
  onClose
}) => {
  const { user } = useAuth();
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterAction, setFilterAction] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [expandedEntries, setExpandedEntries] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (show) {
      void loadGlobalHistory();
    }
  }, [show]);

  const loadGlobalHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await interventionsApi.getGlobalHistory();
      setHistory(data);
    } catch (err) {
      console.error('Erreur lors du chargement de l\'historique global:', err);
      setError('Impossible de charger l\'historique global');
    } finally {
      setLoading(false);
    }
  };

  const parseAppDate = (value: string): Date => {
    const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)
      ? `${value.replace(' ', 'T')}Z`
      : value;
    return new Date(normalized);
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = parseAppDate(dateString);
      if (isNaN(date.getTime())) return dateString;

      const formatter = new Intl.DateTimeFormat('fr-FR', {
        timeZone: 'Europe/Paris',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });

      return formatter.format(date);
    } catch {
      return dateString;
    }
  };

  const getActionBadge = (action: string) => {
    switch (action) {
      case 'CREATE':
        return <span className="badge badge-success">
          <i className="fas fa-plus mr-1"></i>
          Création
        </span>;
      case 'UPDATE':
        return <span className="badge badge-info">
          <i className="fas fa-edit mr-1"></i>
          Modification
        </span>;
      case 'DELETE':
        return <span className="badge badge-danger">
          <i className="fas fa-trash mr-1"></i>
          Suppression
        </span>;
      case 'REQUEST_MODIFICATION':
        return <span className="badge" style={{backgroundColor: '#f97316', color: 'white'}}>
          <i className="fas fa-paper-plane mr-1"></i>
          Demande de modification
        </span>;
      case 'REQUEST_DELETION':
        return <span className="badge" style={{backgroundColor: '#f97316', color: 'white'}}>
          <i className="fas fa-paper-plane mr-1"></i>
          Demande de suppression
        </span>;
      case 'MODIFICATION_APPROVED':
        return <span className="badge" style={{backgroundColor: '#10b981', color: 'white'}}>
          <i className="fas fa-check-circle mr-1"></i>
          Modification approuvée
        </span>;
      case 'DELETION_APPROVED':
        return <span className="badge" style={{backgroundColor: '#10b981', color: 'white'}}>
          <i className="fas fa-check-circle mr-1"></i>
          Suppression approuvée
        </span>;
      case 'MODIFICATION_REJECTED':
        return <span className="badge" style={{backgroundColor: '#ef4444', color: 'white'}}>
          <i className="fas fa-times-circle mr-1"></i>
          Modification rejetée
        </span>;
      case 'DELETION_REJECTED':
        return <span className="badge" style={{backgroundColor: '#ef4444', color: 'white'}}>
          <i className="fas fa-times-circle mr-1"></i>
          Suppression rejetée
        </span>;
      case 'VALIDATED':
        return <span className="badge" style={{backgroundColor: '#059669', color: 'white'}}>
          <i className="fas fa-check-double mr-1"></i>
          Validé par SR
        </span>;
      case 'REJECTED':
        return <span className="badge" style={{backgroundColor: '#dc2626', color: 'white'}}>
          <i className="fas fa-ban mr-1"></i>
          Refusé par SR
        </span>;
      case 'RESENT':
        return <span className="badge" style={{backgroundColor: '#7c3aed', color: 'white'}}>
          <i className="fas fa-redo mr-1"></i>
          Email renvoyé
        </span>;
      case 'APPROVED':
        return <span className="badge" style={{backgroundColor: '#10b981', color: 'white'}}>
          <i className="fas fa-check-circle mr-1"></i>
          Approuvé
        </span>;
      default:
        return <span className="badge badge-gray">{action}</span>;
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'CREATE':
        return 'fas fa-plus-circle text-green-600';
      case 'UPDATE':
        return 'fas fa-edit text-blue-600';
      case 'DELETE':
        return 'fas fa-trash text-red-600';
      case 'REQUEST_MODIFICATION':
        return 'fas fa-paper-plane text-orange-600';
      case 'REQUEST_DELETION':
        return 'fas fa-paper-plane text-orange-600';
      case 'MODIFICATION_APPROVED':
        return 'fas fa-check-circle text-green-600';
      case 'DELETION_APPROVED':
        return 'fas fa-check-circle text-green-600';
      case 'MODIFICATION_REJECTED':
        return 'fas fa-times-circle text-red-600';
      case 'DELETION_REJECTED':
        return 'fas fa-times-circle text-red-600';
      case 'VALIDATED':
        return 'fas fa-check-double text-emerald-600';
      case 'REJECTED':
        return 'fas fa-ban text-red-600';
      case 'RESENT':
        return 'fas fa-redo text-purple-600';
      case 'APPROVED':
        return 'fas fa-check-circle text-green-600';
      default:
        return 'fas fa-circle text-gray-600';
    }
  };

  const calculateWeekNumber = (data: any) => {
    if (!data) return '';

    if (data.numero_semaine) {
      return data.numero_semaine;
    }

      if (data.disponibilite_a_partir) {
        try {
          const date = parseAppDate(data.disponibilite_a_partir);
          if (!isNaN(date.getTime())) {
            const startOfYear = new Date(date.getFullYear(), 0, 1);
            const days = Math.floor((date.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
            return Math.ceil((days + startOfYear.getDay() + 1) / 7);
          }
      } catch (e) {
      }
    }

    return '';
  };

  const getInterventionDetails = (entry: HistoryEntry) => {
    const data = entry.nouvelles_donnees || entry.anciennes_donnees;
    if (!data) {
      return {
        weekNumber: '',
        prestation: entry.prestation_nom || 'N/A',
        contact: entry.contact || 'N/A',
        lieu: entry.site_nom || 'N/A'
      };
    }

    return {
      weekNumber: calculateWeekNumber(data) || entry.numero_semaine || '',
      prestation: data.prestation_nom || entry.prestation_nom || 'N/A',
      contact: data.contact || entry.contact || 'N/A',
      lieu: data.site_nom || entry.site_nom || 'N/A'
    };
  };

  const toggleExpanded = (entryId: number) => {
    const newExpanded = new Set(expandedEntries);
    if (newExpanded.has(entryId)) {
      newExpanded.delete(entryId);
    } else {
      newExpanded.add(entryId);
    }
    setExpandedEntries(newExpanded);
  };

  const formatEnginsStatus = (data: any, statusField: string) => {
    if (!data) return '-';

    try {
      const statusData = data[`${statusField}_donnees`] ? JSON.parse(data[`${statusField}_donnees`]) : {};
      const validationData = data['validation_donnees'] ? JSON.parse(data['validation_donnees']) : {};
      const engins = [data.engin_1, data.engin_2, data.engin_3, data.engin_4].filter(Boolean);

      if (engins.length === 0) return '-';

      const lines = engins.map((engin, index) => {
        const enginId = `engin_${index + 1}`;

        if (statusField !== 'validation' && validationData[enginId] === 'not_feasible') {
          return null;
        }

        const status = statusData[enginId];
        let statusText = '';
        if (status === 'done') {
          statusText = 'Validé \u2713';
        } else if (status === 'not_feasible') {
          statusText = 'Refusé \u00D7';
        } else {
          statusText = 'En attente';
        }
        return `${engin}: ${statusText}`;
      }).filter(Boolean);

      return lines.length > 0 ? lines.join('\n') : '-';
    } catch {
      return '-';
    }
  };

  const renderWithLineBreaks = (text: string) => {
    if (!text || text === '-') return text;
    const lines = text.split('\n');
    if (lines.length === 1) return text;
    return (
      <span style={{ whiteSpace: 'pre-line' }}>
        {lines.map((line, idx) => (
          <span key={idx}>
            {line}
            {idx < lines.length - 1 && <br />}
          </span>
        ))}
      </span>
    );
  };

  const formatValue = (key: string, value: any, data: any) => {
    if (value === null || value === undefined) return '-';

    if (key === 'annulation_client') {
      return value ? '✓' : '×';
    }

    if (value === '') return '-';

    if (key === 'numero_semaine') {
      return calculateWeekNumber(data) || '-';
      } else if (key === 'date_intervention') {
        try {
          const date = parseAppDate(String(value));
          if (!isNaN(date.getTime())) {
            return date.toLocaleDateString('fr-FR', {
              timeZone: 'Europe/Paris',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
        }
      } catch {
        return String(value);
      }
      } else if (key === 'disponibilite_a_partir' || key === 'depart_pour_mission') {
        try {
          const date = parseAppDate(String(value));
          if (!isNaN(date.getTime())) {
            return date.toLocaleString('fr-FR', {
              timeZone: 'Europe/Paris',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }).replace(',', '');
        }
      } catch {
        return String(value);
      }
    }

    return String(value);
  };

  const renderComparisonTable = (entry: HistoryEntry) => {
    const fieldLabels: {[key: string]: string} = {
      numero_semaine: 'Semaine',
      prestation_nom: 'Prestation',
      contact: 'Contact',
      campagne_nom: 'Campagne',
      numero_elan: 'N° ELAN',
      site_nom: 'Lieu',
      disponibilite_a_partir: 'Dispo',
      depart_pour_mission: 'Départ',
      annulation_client: 'Annulation Client',
      statut_validation: 'Validation SR',
      date_intervention: 'Date intervention',
      technicentre_nom: 'TC'
    };

    const fieldOrder = [
      'numero_semaine',
      'prestation_nom',
      'contact',
      'campagne_nom',
      'numero_elan',
      'site_nom',
      'disponibilite_a_partir',
      'depart_pour_mission',
      'annulation_client',
      'statut_validation',
      'date_intervention',
      'technicentre_nom'
    ];

    const getEnginData = (data: any, enginNumber: number) => {
      if (!data) return null;

      const enginKey = `engin_${enginNumber}`;
      const enginName = data[enginKey];
      if (!enginName) return null;

      const veille = data.veille_gps ? (data.veille_gps.includes(enginName) ? '✓' : '') : '';
      const rdv = data.rdv_fait ? (data.rdv_fait.includes(enginName) ? '✓' : '') : '';
      const realise = data.realise ? (data.realise.includes(enginName) ? '✓' : '') : '';

      const motifGps = data.motif_gps || '';
      const motifRdv = data.motif_rdv || '';
      const motifRealise = data.motif_realise || '';

      const commentaires = [motifGps, motifRdv, motifRealise].filter(c => c && c.trim()).join(', ');
      const commentairesText = commentaires ? 'Voir les commentaires' : '';

      return {
        engin: enginName,
        status: `Veille GPS: ${veille} - RDV: ${rdv} - Réalisé: ${realise}`,
        commentaires: commentairesText,
        fullCommentaires: commentaires
      };
    };

    if (entry.type_action === 'CREATE' && entry.nouvelles_donnees) {
      const data = entry.nouvelles_donnees;
      const rows = fieldOrder.map(key => ({
        label: fieldLabels[key] || key,
        value: key === 'statut_validation' ? formatEnginsStatus(data, 'validation') : formatValue(key, data[key], data)
      }));

      rows.push(
        { label: 'GPS', value: formatEnginsStatus(data, 'veille_gps') },
        { label: 'RDV', value: formatEnginsStatus(data, 'rdv_fait') },
        { label: 'Réalisé', value: formatEnginsStatus(data, 'realise') }
      );

      const commentaires: string[] = [];
      if (data.commentaires) {
        commentaires.push(`Général = ${data.commentaires}`);
      }
      try {
        const validationComments = data.commentaires_validation_donnees ? JSON.parse(data.commentaires_validation_donnees) : {};
        Object.keys(validationComments).forEach(enginId => {
          const enginIndex = parseInt(enginId.split('_')[1]) - 1;
          const enginNum = [data.engin_1, data.engin_2, data.engin_3, data.engin_4][enginIndex];
          if (enginNum && validationComments[enginId]) {
            commentaires.push(`${enginNum} = ${validationComments[enginId]}`);
          }
        });
      } catch {}

      return (
        <div className="space-y-1.5 text-xs">
          <div className="grid grid-cols-[120px_1fr] gap-4 font-semibold bg-gray-50 px-3 py-1.5 rounded">
            <div></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-secondary">Nouvelle valeur</div>
              <div className="text-dark">Commentaire</div>
            </div>
          </div>

          {rows.map((row, idx) => (
            <div key={idx} className="grid grid-cols-[120px_1fr] gap-4 px-3 py-1 hover:bg-gray-50 rounded">
              <div className="font-semibold text-gray">{row.label}</div>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-secondary font-medium">{renderWithLineBreaks(row.value)}</div>
                <div className="text-info text-xs">
                  {row.label === 'Validation SR' && commentaires.length > 0 ? renderWithLineBreaks(commentaires.join('\n')) : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (entry.type_action === 'DELETE' && entry.anciennes_donnees) {
      const data = entry.anciennes_donnees;
      const rows = fieldOrder.map(key => ({
        label: fieldLabels[key] || key,
        value: key === 'statut_validation' ? formatEnginsStatus(data, 'validation') : formatValue(key, data[key], data)
      }));

      rows.push(
        { label: 'GPS', value: formatEnginsStatus(data, 'veille_gps') },
        { label: 'RDV', value: formatEnginsStatus(data, 'rdv_fait') },
        { label: 'Réalisé', value: formatEnginsStatus(data, 'realise') }
      );

      return (
        <div className="space-y-1.5 text-xs">
          <div className="grid grid-cols-[120px_1fr] gap-4 font-semibold bg-gray-50 px-3 py-1.5 rounded">
            <div></div>
            <div className="text-danger">Ancienne valeur</div>
          </div>

          {rows.map((row, idx) => (
            <div key={idx} className="grid grid-cols-[120px_1fr] gap-4 px-3 py-1 hover:bg-gray-50 rounded">
              <div className="font-semibold text-gray">{row.label}</div>
              <div className="text-danger font-medium">{renderWithLineBreaks(row.value)}</div>
            </div>
          ))}
        </div>
      );
    }

    if (entry.type_action === 'UPDATE' && entry.anciennes_donnees && entry.nouvelles_donnees) {
      const oldData = entry.anciennes_donnees;
      const newData = entry.nouvelles_donnees;
      const basicFields = fieldOrder.map(key => {
        const oldValue = oldData[key];
        const newValue = newData[key];
        let displayOldValue = '';
        let displayNewValue = '';

        if (key === 'numero_semaine') {
          displayOldValue = calculateWeekNumber(oldData) || '';
          displayNewValue = calculateWeekNumber(newData) || '';
        } else {
          if (oldValue) {
            displayOldValue = String(oldValue);
                if (key.includes('date') || key === 'disponibilite_a_partir' || key === 'depart_pour_mission') {
                try {
                  const date = parseAppDate(String(oldValue));
                  if (!isNaN(date.getTime())) {
                    displayOldValue = date.toLocaleString('fr-FR', {
                      day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  });
                }
              } catch (e) {
                displayOldValue = String(oldValue);
              }
            }
          }

          if (newValue) {
            displayNewValue = String(newValue);
                if (key.includes('date') || key === 'disponibilite_a_partir' || key === 'depart_pour_mission') {
                try {
                  const date = parseAppDate(String(newValue));
                  if (!isNaN(date.getTime())) {
                    displayNewValue = date.toLocaleString('fr-FR', {
                      day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  });
                }
              } catch (e) {
                displayNewValue = String(newValue);
              }
            }
          }
        }

        return {
          field: fieldLabels[key] || key,
          oldValue: displayOldValue,
          newValue: displayNewValue
        };
      });

      const commentaires: string[] = [];
      if (newData.commentaires) {
        commentaires.push(`Général = ${newData.commentaires}`);
      }
      try {
        const validationComments = newData.commentaires_validation_donnees ? JSON.parse(newData.commentaires_validation_donnees) : {};
        Object.keys(validationComments).forEach(enginId => {
          const enginIndex = parseInt(enginId.split('_')[1]) - 1;
          const enginNum = [newData.engin_1, newData.engin_2, newData.engin_3, newData.engin_4][enginIndex];
          if (enginNum && validationComments[enginId]) {
            commentaires.push(`${enginNum} = ${validationComments[enginId]}`);
          }
        });
      } catch {}

      const rows = fieldOrder.map(key => {
        let oldVal = key === 'statut_validation' ? formatEnginsStatus(oldData, 'validation') : formatValue(key, oldData[key], oldData);
        let newVal = key === 'statut_validation' ? formatEnginsStatus(newData, 'validation') : formatValue(key, newData[key], newData);

        if (key === 'numero_semaine' && (!oldVal || oldVal === '-')) {
          oldVal = calculateWeekNumber(oldData) || '-';
        }
        if (key === 'numero_semaine' && (!newVal || newVal === '-')) {
          newVal = calculateWeekNumber(newData) || '-';
        }

        return {
          label: fieldLabels[key] || key,
          oldValue: oldVal,
          newValue: newVal,
          changed: oldVal !== newVal
        };
      });

      const oldGps = formatEnginsStatus(oldData, 'veille_gps');
      const newGps = formatEnginsStatus(newData, 'veille_gps');
      const oldRdv = formatEnginsStatus(oldData, 'rdv_fait');
      const newRdv = formatEnginsStatus(newData, 'rdv_fait');
      const oldRealise = formatEnginsStatus(oldData, 'realise');
      const newRealise = formatEnginsStatus(newData, 'realise');

      rows.push(
        { label: 'GPS', oldValue: oldGps, newValue: newGps, changed: oldGps !== newGps },
        { label: 'RDV', oldValue: oldRdv, newValue: newRdv, changed: oldRdv !== newRdv },
        { label: 'Réalisé', oldValue: oldRealise, newValue: newRealise, changed: oldRealise !== newRealise }
      );

      return (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-3 py-2 text-left font-bold text-dark border-b-2">Champ</th>
                <th className="px-3 py-2 text-left font-bold text-danger border-b-2">Avant</th>
                <th className="px-3 py-2 text-left font-bold text-secondary border-b-2">Après</th>
                <th className="px-3 py-2 text-left font-bold text-dark border-b-2">Commentaire</th>
              </tr>
            </thead>
            <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} className={`border-t hover:bg-gray-50 ${row.changed ? 'bg-yellow-100' : ''}`}>
              <td className="px-3 py-2 font-semibold text-gray">{row.label}</td>
              <td className={`px-3 py-2 font-medium ${row.changed ? 'text-red-600 font-bold bg-red-50' : 'text-gray-600'}`} style={{ whiteSpace: 'pre-line' }}>
                {row.oldValue}
              </td>
              <td className={`px-3 py-2 font-medium ${row.changed ? 'text-green-600 font-bold bg-green-50' : 'text-gray-600'}`} style={{ whiteSpace: 'pre-line' }}>
                {row.newValue}
              </td>
              <td className="px-3 py-2 text-info text-xs" style={{ whiteSpace: 'pre-line' }}>
                {row.label === 'Validation SR' && commentaires.length > 0 ? commentaires.join('\n') : ''}
              </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (entry.type_action === 'REQUEST_MODIFICATION' && entry.anciennes_donnees && entry.nouvelles_donnees) {
      const actualData = entry.anciennes_donnees;
      const requestData = entry.nouvelles_donnees;

      const commentaires: string[] = [];
      if (requestData.commentaires) {
        commentaires.push(`Général = ${requestData.commentaires}`);
      }

      const rows = fieldOrder.map(key => {
        let actualVal = key === 'statut_validation' ? formatEnginsStatus(actualData, 'validation') : formatValue(key, actualData[key], actualData);
        let requestVal = key === 'statut_validation' ? formatEnginsStatus(requestData, 'validation') : formatValue(key, requestData[key], requestData);

        if (key === 'numero_semaine') {
          if (!actualVal || actualVal === '-') actualVal = calculateWeekNumber(actualData) || '-';
          if (!requestVal || requestVal === '-') requestVal = calculateWeekNumber(requestData) || '-';
        }

        return {
          label: fieldLabels[key] || key,
          actualValue: actualVal,
          requestValue: requestVal,
          changed: actualVal !== requestVal
        };
      });

      rows.push(
        { label: 'GPS', actualValue: formatEnginsStatus(actualData, 'veille_gps'), requestValue: formatEnginsStatus(requestData, 'veille_gps'), changed: false },
        { label: 'RDV', actualValue: formatEnginsStatus(actualData, 'rdv_fait'), requestValue: formatEnginsStatus(requestData, 'rdv_fait'), changed: false },
        { label: 'Réalisé', actualValue: formatEnginsStatus(actualData, 'realise'), requestValue: formatEnginsStatus(requestData, 'realise'), changed: false }
      );

                return (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-3 py-2 text-left font-bold text-dark border-b-2">Champ</th>
                <th className="px-3 py-2 text-left font-bold text-orange-600 border-b-2">Demande</th>
                <th className="px-3 py-2 text-left font-bold text-secondary border-b-2">Actuel</th>
                <th className="px-3 py-2 text-left font-bold text-dark border-b-2">Commentaire</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} className={`border-t hover:bg-gray-50 ${row.changed ? 'bg-orange-50' : ''}`}>
                  <td className="px-3 py-2 font-semibold text-gray">{row.label}</td>
                  <td className={`px-3 py-2 font-medium ${row.changed ? 'text-orange-600 font-bold bg-orange-100' : 'text-gray-600'}`} style={{ whiteSpace: 'pre-line' }}>
                    {row.requestValue}
                    </td>
                  <td className={`px-3 py-2 font-medium ${row.changed ? 'text-gray-700' : 'text-gray-600'}`} style={{ whiteSpace: 'pre-line' }}>
                    {row.actualValue}
                    </td>
                  <td className="px-3 py-2 text-info text-xs" style={{ whiteSpace: 'pre-line' }}>
                    {row.label === 'Validation SR' && commentaires.length > 0 ? commentaires.join('\n') : ''}
                    </td>
                  </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (entry.type_action === 'MODIFICATION_APPROVED' && entry.anciennes_donnees && entry.nouvelles_donnees) {
      const actualData = entry.nouvelles_donnees;
      const approvedData = entry.anciennes_donnees;

      const commentaires: string[] = [];
      if (approvedData.commentaires) {
        commentaires.push(`Général = ${approvedData.commentaires}`);
      }

      const rows = fieldOrder.map(key => {
        let actualVal = key === 'statut_validation' ? formatEnginsStatus(actualData, 'validation') : formatValue(key, actualData[key], actualData);
        let approvedVal = key === 'statut_validation' ? formatEnginsStatus(approvedData, 'validation') : formatValue(key, approvedData[key], approvedData);

        if (key === 'numero_semaine') {
          if (!actualVal || actualVal === '-') actualVal = calculateWeekNumber(actualData) || '-';
          if (!approvedVal || approvedVal === '-') approvedVal = calculateWeekNumber(approvedData) || '-';
        }

        return {
          label: fieldLabels[key] || key,
          actualValue: actualVal,
          approvedValue: approvedVal,
          changed: actualVal !== approvedVal
        };
      });

      rows.push(
        { label: 'GPS', actualValue: formatEnginsStatus(actualData, 'veille_gps'), approvedValue: formatEnginsStatus(approvedData, 'veille_gps'), changed: false },
        { label: 'RDV', actualValue: formatEnginsStatus(actualData, 'rdv_fait'), approvedValue: formatEnginsStatus(approvedData, 'rdv_fait'), changed: false },
        { label: 'Réalisé', actualValue: formatEnginsStatus(actualData, 'realise'), approvedValue: formatEnginsStatus(approvedData, 'realise'), changed: false }
      );

      return (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-3 py-2 text-left font-bold text-dark border-b-2">Champ</th>
                <th className="px-3 py-2 text-left font-bold text-secondary border-b-2">Actuel</th>
                <th className="px-3 py-2 text-left font-bold text-green-600 border-b-2">Demande approuvée</th>
                <th className="px-3 py-2 text-left font-bold text-dark border-b-2">Commentaire</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} className={`border-t hover:bg-gray-50 ${row.changed ? 'bg-green-50' : ''}`}>
                  <td className="px-3 py-2 font-semibold text-gray">{row.label}</td>
                  <td className={`px-3 py-2 font-medium ${row.changed ? 'text-gray-700' : 'text-gray-600'}`} style={{ whiteSpace: 'pre-line' }}>
                    {row.actualValue}
                  </td>
                  <td className={`px-3 py-2 font-medium ${row.changed ? 'text-green-600 font-bold bg-green-100' : 'text-gray-600'}`} style={{ whiteSpace: 'pre-line' }}>
                    {row.approvedValue}
                  </td>
                  <td className="px-3 py-2 text-info text-xs" style={{ whiteSpace: 'pre-line' }}>
                    {row.label === 'Validation SR' && commentaires.length > 0 ? commentaires.join('\n') : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (entry.type_action === 'MODIFICATION_REJECTED' && entry.anciennes_donnees && entry.nouvelles_donnees) {
      const rejectedData = entry.anciennes_donnees;
      const actualData = entry.nouvelles_donnees;

      const commentaires: string[] = [];
      if (rejectedData.commentaires) {
        commentaires.push(`Général = ${rejectedData.commentaires}`);
      }

      const rows = fieldOrder.map(key => {
        let rejectedVal = key === 'statut_validation' ? formatEnginsStatus(rejectedData, 'validation') : formatValue(key, rejectedData[key], rejectedData);
        let actualVal = key === 'statut_validation' ? formatEnginsStatus(actualData, 'validation') : formatValue(key, actualData[key], actualData);

        if (key === 'numero_semaine') {
          if (!rejectedVal || rejectedVal === '-') rejectedVal = calculateWeekNumber(rejectedData) || '-';
          if (!actualVal || actualVal === '-') actualVal = calculateWeekNumber(actualData) || '-';
        }

        return {
          label: fieldLabels[key] || key,
          rejectedValue: rejectedVal,
          actualValue: actualVal,
          changed: rejectedVal !== actualVal
        };
      });

      rows.push(
        { label: 'GPS', rejectedValue: formatEnginsStatus(rejectedData, 'veille_gps'), actualValue: formatEnginsStatus(actualData, 'veille_gps'), changed: false },
        { label: 'RDV', rejectedValue: formatEnginsStatus(rejectedData, 'rdv_fait'), actualValue: formatEnginsStatus(actualData, 'rdv_fait'), changed: false },
        { label: 'Réalisé', rejectedValue: formatEnginsStatus(rejectedData, 'realise'), actualValue: formatEnginsStatus(actualData, 'realise'), changed: false }
      );

      return (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-3 py-2 text-left font-bold text-dark border-b-2">Champ</th>
                <th className="px-3 py-2 text-left font-bold text-red-600 border-b-2">Demande rejetée</th>
                <th className="px-3 py-2 text-left font-bold text-secondary border-b-2">Actuel</th>
                <th className="px-3 py-2 text-left font-bold text-dark border-b-2">Commentaire</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} className={`border-t hover:bg-gray-50 ${row.changed ? 'bg-red-50' : ''}`}>
                  <td className="px-3 py-2 font-semibold text-gray">{row.label}</td>
                  <td className={`px-3 py-2 font-medium ${row.changed ? 'text-red-600 font-bold bg-red-100' : 'text-gray-600'}`} style={{ whiteSpace: 'pre-line' }}>
                    {row.rejectedValue}
                  </td>
                  <td className={`px-3 py-2 font-medium ${row.changed ? 'text-gray-700' : 'text-gray-600'}`} style={{ whiteSpace: 'pre-line' }}>
                    {row.actualValue}
                  </td>
                  <td className="px-3 py-2 text-info text-xs" style={{ whiteSpace: 'pre-line' }}>
                    {row.label === 'Validation SR' && commentaires.length > 0 ? commentaires.join('\n') : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (entry.type_action === 'VALIDATED' && entry.nouvelles_donnees) {
      const data = entry.nouvelles_donnees;
      const rows = fieldOrder.map(key => {
        let value = key === 'statut_validation' ? formatEnginsStatus(data, 'validation') : formatValue(key, data[key], data);

        if (key === 'numero_semaine' && (!value || value === '-')) {
          value = calculateWeekNumber(data) || '-';
        }

        return {
          label: fieldLabels[key] || key,
          value: value
        };
      });

      rows.push(
        { label: 'GPS', value: formatEnginsStatus(data, 'veille_gps') },
        { label: 'RDV', value: formatEnginsStatus(data, 'rdv_fait') },
        { label: 'Réalisé', value: formatEnginsStatus(data, 'realise') }
      );

      const commentaires: string[] = [];
      if (data.commentaires) {
        commentaires.push(`Général = ${data.commentaires}`);
      }
      try {
        const validationComments = data.commentaires_validation_donnees ? JSON.parse(data.commentaires_validation_donnees) : {};
        Object.keys(validationComments).forEach(enginId => {
          const enginIndex = parseInt(enginId.split('_')[1]) - 1;
          const enginNum = [data.engin_1, data.engin_2, data.engin_3, data.engin_4][enginIndex];
          if (enginNum && validationComments[enginId]) {
            commentaires.push(`${enginNum} = ${validationComments[enginId]}`);
          }
        });
      } catch {}

      return (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-3 py-2 text-left font-bold text-dark border-b-2">Champ</th>
                <th className="px-3 py-2 text-left font-bold text-green-600 border-b-2">Validé par SR</th>
                <th className="px-3 py-2 text-left font-bold text-dark border-b-2">Commentaire</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} className="border-t hover:bg-gray-50 bg-green-50">
                  <td className="px-3 py-2 font-semibold text-gray">{row.label}</td>
                  <td className="px-3 py-2 font-medium text-green-600 font-bold" style={{ whiteSpace: 'pre-line' }}>{row.value}</td>
                  <td className="px-3 py-2 text-info text-xs" style={{ whiteSpace: 'pre-line' }}>
                    {row.label === 'Validation SR' && commentaires.length > 0 ? commentaires.join('\n') : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (entry.type_action === 'REJECTED' && entry.nouvelles_donnees) {
      const data = entry.nouvelles_donnees;
      const rows = fieldOrder.map(key => {
        let value = key === 'statut_validation' ? formatEnginsStatus(data, 'validation') : formatValue(key, data[key], data);

        if (key === 'numero_semaine' && (!value || value === '-')) {
          value = calculateWeekNumber(data) || '-';
        }

        return {
          label: fieldLabels[key] || key,
          value: value
        };
      });

      rows.push(
        { label: 'GPS', value: formatEnginsStatus(data, 'veille_gps') },
        { label: 'RDV', value: formatEnginsStatus(data, 'rdv_fait') },
        { label: 'Réalisé', value: formatEnginsStatus(data, 'realise') }
      );

      const commentaires: string[] = [];
      if (data.commentaires) {
        commentaires.push(`Général = ${data.commentaires}`);
      }
      if (data.motif_refus) {
        commentaires.push(`Motif refus = ${data.motif_refus}`);
      }
      try {
        const validationComments = data.commentaires_validation_donnees ? JSON.parse(data.commentaires_validation_donnees) : {};
        Object.keys(validationComments).forEach(enginId => {
          const enginIndex = parseInt(enginId.split('_')[1]) - 1;
          const enginNum = [data.engin_1, data.engin_2, data.engin_3, data.engin_4][enginIndex];
          if (enginNum && validationComments[enginId]) {
            commentaires.push(`${enginNum} = ${validationComments[enginId]}`);
          }
        });
      } catch {}

      return (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-3 py-2 text-left font-bold text-dark border-b-2">Champ</th>
                <th className="px-3 py-2 text-left font-bold text-red-600 border-b-2">Refusé par SR</th>
                <th className="px-3 py-2 text-left font-bold text-dark border-b-2">Commentaire</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} className="border-t hover:bg-gray-50 bg-red-50">
                  <td className="px-3 py-2 font-semibold text-gray">{row.label}</td>
                  <td className="px-3 py-2 font-medium text-red-600 font-bold" style={{ whiteSpace: 'pre-line' }}>{row.value}</td>
                  <td className="px-3 py-2 text-info text-xs" style={{ whiteSpace: 'pre-line' }}>
                    {commentaires.length > 0 ? commentaires.join('\n') : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (entry.nouvelles_donnees || entry.anciennes_donnees) {
      const data = entry.nouvelles_donnees || entry.anciennes_donnees;
      const rows = fieldOrder.map(key => {
        let value = key === 'statut_validation' ? formatEnginsStatus(data, 'validation') : formatValue(key, data[key], data);

        if (key === 'numero_semaine' && (!value || value === '-')) {
          value = calculateWeekNumber(data) || '-';
        }

        return {
          label: fieldLabels[key] || key,
          value: value
        };
      });

      rows.push(
        { label: 'GPS', value: formatEnginsStatus(data, 'veille_gps') },
        { label: 'RDV', value: formatEnginsStatus(data, 'rdv_fait') },
        { label: 'Réalisé', value: formatEnginsStatus(data, 'realise') }
      );

      const commentaires: string[] = [];
      if (data.commentaires) {
        commentaires.push(`Général = ${data.commentaires}`);
      }
      try {
        const validationComments = data.commentaires_validation_donnees ? JSON.parse(data.commentaires_validation_donnees) : {};
        Object.keys(validationComments).forEach(enginId => {
          const enginIndex = parseInt(enginId.split('_')[1]) - 1;
          const enginNum = [data.engin_1, data.engin_2, data.engin_3, data.engin_4][enginIndex];
          if (enginNum && validationComments[enginId]) {
            commentaires.push(`${enginNum} = ${validationComments[enginId]}`);
          }
        });
      } catch {}

      return (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-3 py-2 text-left font-bold text-dark border-b-2">Champ</th>
                <th className="px-3 py-2 text-left font-bold text-secondary border-b-2">Valeur</th>
                <th className="px-3 py-2 text-left font-bold text-dark border-b-2">Commentaire</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} className="border-t hover:bg-gray-50">
                  <td className="px-3 py-2 font-semibold text-gray">{row.label}</td>
                  <td className="px-3 py-2 text-secondary font-medium" style={{ whiteSpace: 'pre-line' }}>{row.value}</td>
                  <td className="px-3 py-2 text-info text-xs" style={{ whiteSpace: 'pre-line' }}>
                    {row.label === 'Validation SR' && commentaires.length > 0 ? commentaires.join('\n') : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return null;
  };

  const filteredHistory = history.filter(entry => {
    if (!user?.isGOF) {
      const allowedTypes = ['REQUEST_MODIFICATION', 'MODIFICATION_APPROVED', 'MODIFICATION_REJECTED'];
      if (!allowedTypes.includes(entry.type_action)) {
        return false;
      }
    }

    const matchesAction = filterAction === '' || entry.type_action === filterAction;
    const details = getInterventionDetails(entry);
    const matchesSearch = searchTerm === '' ||
      details.contact.toLowerCase().includes(searchTerm.toLowerCase()) ||
      details.lieu.toLowerCase().includes(searchTerm.toLowerCase()) ||
      details.prestation.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.intervention_id.toString().includes(searchTerm) ||
      (details.weekNumber && details.weekNumber.toString().includes(searchTerm));

    return matchesAction && matchesSearch;
  });

  if (!show) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content modal-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-bold text-dark mb-2">
              <i className="fas fa-history mr-3 text-primary"></i>
              Historique des Interventions
            </h3>
            <p className="text-gray">
              Suivi de toutes les modifications, créations et suppressions
            </p>
          </div>
          <button onClick={onClose} className="modal-close">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="overflow-y-auto">
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-64">
                <label className="form-label">
                  <i className="fas fa-search mr-2 text-primary"></i>
                  Rechercher
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Semaine, prestation, contact, lieu ou ID..."
                  className="form-input"
                />
              </div>
              <div className="min-w-48">
                <label className="form-label">
                  <i className="fas fa-filter mr-2 text-primary"></i>
                  Type d'action
                </label>
                <select
                  value={filterAction}
                  onChange={(e) => setFilterAction(e.target.value)}
                  className="form-select"
                >
                  <option value="">Toutes les actions</option>
                  <option value="CREATE">Créations</option>
                  <option value="UPDATE">Modifications</option>
                  <option value="DELETE">Suppressions</option>
                  <option value="VALIDATED">Validé par SR</option>
                  <option value="REJECTED">Refusé par SR</option>
                  <option value="REQUEST_MODIFICATION">Demande de modification</option>
                  <option value="MODIFICATION_APPROVED">Modification approuvée</option>
                  <option value="MODIFICATION_REJECTED">Modification rejetée</option>
                </select>
              </div>
            </div>
          </div>

          {loading && (
            <div className="text-center py-16">
              <div className="loading-spinner mx-auto mb-4"></div>
              <p className="text-gray">Chargement de l'historique...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-danger text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-exclamation-triangle text-xl"></i>
              </div>
              <p className="text-danger mb-6">{error}</p>
              <button
                onClick={loadGlobalHistory}
                className="btn btn-primary"
              >
                <i className="fas fa-redo mr-2"></i>
                Réessayer
              </button>
            </div>
          )}

          {!loading && !error && filteredHistory.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-history text-xl text-gray"></i>
              </div>
              <p className="text-dark text-xl font-bold mb-3">Aucun historique trouvé</p>
              <p className="text-gray">
                {history.length === 0 ?
                  "Aucune modification d'intervention enregistrée" :
                  "Aucun résultat ne correspond à vos critères de recherche"
                }
              </p>
            </div>
          )}

          {!loading && !error && filteredHistory.length > 0 && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg px-4 py-2">
                <p className="text-gray text-sm">
                  <i className="fas fa-list mr-2 text-primary"></i>
                  {filteredHistory.length} entrée{filteredHistory.length > 1 ? 's' : ''}
                  {history.length !== filteredHistory.length && ` sur ${history.length} au total`}
                </p>
              </div>

              {filteredHistory.map((entry) => (
                <div
                  key={entry.id}
                  className="card"
                >
                  <div
                    className="card-body cursor-pointer"
                    onClick={() => toggleExpanded(entry.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="stat-icon">
                          <i className={`${getActionIcon(entry.type_action)} text-primary`}></i>
                        </div>
                        <div>
                          <div className="flex items-center space-x-3 mb-2">
                            {getActionBadge(entry.type_action)}
                            <span className="font-bold text-dark">
                              Semaine {getInterventionDetails(entry).weekNumber || 'N/A'} • {getInterventionDetails(entry).prestation}
                            </span>
                          </div>
                          <div className="text-gray text-sm mb-2">
                            {getInterventionDetails(entry).contact} • {getInterventionDetails(entry).lieu}
                          </div>
                          <div className="flex items-center space-x-4 text-sm text-light">
                            <span>
                              <i className="fas fa-clock mr-1"></i>
                              {formatDateTime(entry.modifie_le)}
                            </span>
                            {entry.utilisateur_id && (
                              <span>
                                <i className="fas fa-user mr-1"></i>
                                {entry.role_utilisateur === 'gof' ? 'GOF' : entry.role_utilisateur === 'utilisateur' ? 'Utilisateur' : entry.role_utilisateur}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="text-primary">
                        <i className={`fas ${expandedEntries.has(entry.id) ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                      </div>
                    </div>
                  </div>

                  {expandedEntries.has(entry.id) && (
                    <div className="card-footer">
                      {renderComparisonTable(entry)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default HistoriqueGlobalModal;
